import { Store } from '@ngxs/store';
import { defer, Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ignoreNullish, Loadable, Logger } from '@aiwyn/shared';
import { BaseContact } from '@aiwyn/shared';

import { ClientRequestAssigneeModel, TypedIdentity } from '../models';
import { AssigneeIdentityType } from '../utility';
import { CommonClientRequestsState } from '../state';

import { AbstractBaseCommonClientRequestController } from './abstract-base-common-client-request-controller.model';

export class BaseCommonClientRequestAssignee<
  Assignee extends ClientRequestAssigneeModel = ClientRequestAssigneeModel,
  AssigneeTypeClass extends CommonAssigneeModelToClassMap<Assignee> = CommonAssigneeModelToClassMap<Assignee>,
> extends AbstractBaseCommonClientRequestController<TypedIdentity<AssigneeIdentityType>, Assignee, AssigneeTypeClass> {

  readonly model$: Observable<Loadable<Assignee>> = defer(() =>
    this._store.select(CommonClientRequestsState.getAssigneeLoadable(this.identity)),
  ).pipe(
    ignoreNullish(),
  ) as Observable<Loadable<Assignee>>;

  protected _create$(
    model$: Observable<Loadable<Assignee>>,
    store: Store,
    logger: Logger,
  ): Observable<AssigneeTypeClass> {
    return model$.pipe(
      ignoreNullish(),
      map(_ => new CommonClientRequestAssigneeContact(model$, store, logger) as AssigneeTypeClass),
    );
  }
}

abstract class AbstractCommonClientRequestAssignee<
  Assignee extends ClientRequestAssigneeModel = ClientRequestAssigneeModel,
> {
  constructor(
    protected readonly model$: Observable<Loadable<Assignee>>,
    protected readonly _store: Store,
    protected readonly _logger: Logger,
  ) {}

  abstract name$: Observable<string>;
  abstract email$: Observable<string>;
}

export class CommonClientRequestAssigneeContact extends AbstractCommonClientRequestAssignee<BaseContact> {
  name$ = this.model$.pipe(
    map(({ data }) => data?.contactInfo.fullName),
  );

  email$ = this.model$.pipe(
    map(({ data }) => data?.contactInfo.email),
  );
}

export type CommonClientRequestAssignee = CommonClientRequestAssigneeContact; // | CommonClientRequestAssigneeInvitee;

export type CommonAssigneeModelToClassMap<Assignee extends ClientRequestAssigneeModel> =
  Assignee extends BaseContact ? CommonClientRequestAssigneeContact : never;
